/**
 * API Service
 *
 * Handles all API communications directly with external GTA GSM API
 */
import type {
  ApiConfig,
  TrackingRequest,
  TrackingResponse,
  EmailSubscriptionRequest,
} from '@/types';

export class ApiService {
  private config: ApiConfig;

  constructor(config: ApiConfig) {
    this.config = config;
  }

  /**
   * Get base domain/origin for API calls
   */
  public getBaseUrl(): string {
    try {
      const url = new URL(this.config.url);
      return url.origin;
    } catch {
      // Fallback: strip path if possible
      return (this.config.url || '').replace(/\/+$/, '');
    }
  }

  /**
   * Build an endpoint URL using the same domain and path prefix as the main API URL.
   * Keeps any path prefix (e.g., /api/v1/trackings) while swapping the final segment.
   */
  public buildEndpoint(relativePath: string): string {
    try {
      const base = new URL(this.config.url);
      const cleanedRelative = relativePath.replace(/^\/+/, '');
      const segments = base.pathname.split('/').filter(Boolean);
      // Remove the last segment (e.g., track-my-package) to keep the base prefix
      segments.pop();
      base.pathname = `/${[...segments, cleanedRelative].join('/')}`;
      base.search = '';
      return base.toString();
    } catch {
      // Fallback: best effort concatenation
      const normalizedBase = (this.config.url || '').replace(/\/+$/, '');
      const normalizedRel = relativePath.replace(/^\/+/, '');
      return `${normalizedBase}/${normalizedRel}`;
    }
  }

  /**
   * Get tracking data directly from GTA GSM API
   *
   * @param request Tracking request data
   * @returns Promise with API response containing tracking data
   */
  public async getTrackingData(request: TrackingRequest): Promise<TrackingResponse> {
    try {
      // Validate tracking numbers
      const validation = this.validateTrackingNumbers(request.trackingNumbers);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.errors.join(', '),
        };
      }

      // Prepare request data according to GTA GSM API specification
      const requestData = {
        PageSize: Math.min(request.trackingNumbers.length, 1000),
        Page: 1,
        TrackingNumbers: request.trackingNumbers,
        DateFilterType: 0, // ByCreateDate
        IncludeHistory: true,
        ...(request.postalCode && { ToPostalCodes: [request.postalCode] }),
      };

      // Make direct API request
      const response = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-key': this.config.accountKey,
          'x-api-key': this.config.key,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.IsSuccess) {
        return {
          success: false,
          error: result.ErrorMessage || 'API returned an error',
        };
      }

      // Process the response
      const shipments = result.Data || [];
      const foundNumbers = shipments.map((shipment: any) => shipment.TrackingNumber);
      const notFoundNumbers = request.trackingNumbers.filter(
        (tn) => !foundNumbers.includes(tn)
      );

      return {
        success: true,
        data: shipments,
        metadata: {
          requested_count: request.trackingNumbers.length,
          found_count: shipments.length,
          not_found_count: notFoundNumbers.length,
          requested_numbers: request.trackingNumbers,
          found_numbers: foundNumbers,
          not_found_numbers: notFoundNumbers,
        },
      };
    } catch (error) {
      console.error('[ApiService] Tracking request failed:', error);
      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Subscribe to email notifications for a tracking number
   * Calls external GTA GSM API directly
   *
   * @param request Email subscription request
   * @returns Promise with subscription result
   */
  public async subscribeToEmailNotifications(
    request: EmailSubscriptionRequest
  ): Promise<TrackingResponse> {
    try {
      // Prepare request data for GTA GSM email subscription API
      const requestData = {
        TrackingNumber: request.trackingNumber,
        EmailAddress: request.emailAddress,
        PostalCode: request.postalCode || '',
      };

      // Make direct API request to GTA GSM email subscription endpoint
      const response = await fetch(`${this.config.url}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-key': this.config.accountKey,
          'x-api-key': this.config.key,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.IsSuccess) {
        return {
          success: false,
          error: result.ErrorMessage || 'Email subscription failed',
        };
      }

      return {
        success: true,
        data: result.Data,
      };
    } catch (error) {
      console.error('[ApiService] Email subscription failed:', error);
      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Unsubscribe from email notifications
   * Calls external GTA GSM API directly
   *
   * @param trackingNumber Tracking number
   * @param emailAddress Email address
   * @returns Promise with unsubscription result
   */
  public async unsubscribeFromEmailNotifications(
    trackingNumber: string,
    emailAddress: string
  ): Promise<TrackingResponse> {
    try {
      // Prepare request data for GTA GSM email unsubscription API
      const requestData = {
        TrackingNumber: trackingNumber,
        EmailAddress: emailAddress,
      };

      // Make direct API request to GTA GSM email unsubscription endpoint
      const response = await fetch(`${this.config.url}/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-key': this.config.accountKey,
          'x-api-key': this.config.key,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.IsSuccess) {
        return {
          success: false,
          error: result.ErrorMessage || 'Email unsubscription failed',
        };
      }

      return {
        success: true,
        data: result.Data,
      };
    } catch (error) {
      console.error('[ApiService] Email unsubscription failed:', error);
      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    }
  }

  /**
   * Validate tracking numbers format
   *
   * @param trackingNumbers Array of tracking numbers
   * @returns Validation result
   */
  public validateTrackingNumbers(trackingNumbers: string[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(trackingNumbers) || trackingNumbers.length === 0) {
      errors.push('At least one tracking number is required');
      return { valid: false, errors };
    }

    // Check each tracking number
    trackingNumbers.forEach((trackingNumber, index) => {
      const trimmed = trackingNumber.trim();

      if (!trimmed) {
        errors.push(`Tracking number ${index + 1} is empty`);
        return;
      }

      if (trimmed.length < 3) {
        errors.push(`Tracking number ${index + 1} is too short`);
        return;
      }

      if (trimmed.length > 50) {
        errors.push(`Tracking number ${index + 1} is too long`);
        return;
      }

      // Basic format validation (alphanumeric and common special characters)
      if (!/^[a-zA-Z0-9\-_]+$/.test(trimmed)) {
        errors.push(`Tracking number ${index + 1} contains invalid characters`);
      }
    });

    // Check total count
    if (trackingNumbers.length > this.config.maxTrackingNumbers) {
      errors.push(`Maximum ${this.config.maxTrackingNumbers} tracking numbers allowed`);
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate email address format
   *
   * @param email Email address
   * @returns Whether email is valid
   */
  public validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Parse tracking numbers from text input
   *
   * @param input Text input with tracking numbers
   * @returns Array of parsed tracking numbers
   */
  public parseTrackingNumbers(input: string): string[] {
    if (!input || typeof input !== 'string') {
      return [];
    }

    // Split by common separators and clean up
    return input
      .split(/[\s,\n\r]+/)
      .map((tn) => tn.trim())
      .filter((tn) => tn.length > 0)
      .slice(0, this.config.maxTrackingNumbers); // Limit to max allowed
  }

  /**
   * Get API configuration
   *
   * @returns Current API configuration
   */
  public getConfig(): ApiConfig {
    return { ...this.config };
  }

  /**
   * Update API configuration
   *
   * @param newConfig Partial configuration to update
   */
  public updateConfig(newConfig: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Check if API service is properly configured
   *
   * @returns Whether service is configured
   */
  public isConfigured(): boolean {
    return !!(
      this.config.url &&
      this.config.key &&
      this.config.accountKey
    );
  }

  /**
   * Get human-readable error message
   *
   * @param error Error object or string
   * @returns User-friendly error message
   */
  public getErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      const errorObj = error as Record<string, unknown>;

      if (typeof errorObj.message === 'string') {
        return errorObj.message;
      }

      if (typeof errorObj.error === 'string') {
        return errorObj.error;
      }
    }

    return 'An unexpected error occurred';
  }
}